import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { ApplicantsTableCell } from '@framework/common-web/schema/submissions/applicants/ApplicantsTableCell.tsx';
import { TaskName } from '@framework/common-web/ui/tag/TaskName.tsx';
import { SubmissionLogic } from '@framework/frontoffice-submission/submission/SubmissionLogic.ts';
import { SubmissionStateTag } from '@framework/frontoffice-submission/submission/state/SubmissionStateTag.tsx';
import { t } from '@framework/i18n/intl/i18next.ts';
import { ISODate } from '@framework/ui/components/date/ISODate.tsx';
import { TableCellEllipsis } from '@framework/ui/components/table/TableCellEllipsis.tsx';
import { type ColumnsType, type ColumnType } from 'antd/es/table';
import { type ColumnGroupType } from 'antd/lib/table';
import { SubmissionAgidColumns } from '#features/packages/xsd-web/src/frontoffice/submission/columns/SubmissionAgidColumns.tsx';
import { theme } from '#theme';

const dateFormat = 'DD/MM/YYYY HH:mm';

export const submissionsColumnsBase = {
  idTipoDomanda: {
    title: t('submission-columns.domanda', 'Domanda'),
    dataIndex: 'idTipoDomanda',
    key: 'idTipoDomanda',
    sorter: true,
    sortDataIndex: 'idTipoDomanda',
    render: (_, record) => record.idTipoDomanda,
  },
  titoloTipoDomanda: {
    title: t('submission-columns.domanda', 'Domanda'),
    dataIndex: 'titoloTipoDomanda',
    key: 'titoloTipoDomanda',
    sorter: true,
    widthWithActiveScroll: 210,
    sortDataIndex: 'titoloTipoDomanda',
    render: (_, record) => (
      <TableCellEllipsis tooltip={false}>
        {record.titoloTipoDomanda}
      </TableCellEllipsis>
    ),
  },
  stato: {
    title: t('submission-columns.stato', 'Stato'),
    dataIndex: 'statoRichiesta',
    key: 'statoRichiesta',
    sorter: true,
    sortDataIndex: 'statoRichiesta',
    render: (_, record) => <SubmissionStateTag submission={record} />,
  },
  fase: {
    title: t('submission-columns.fase', 'Fase'),
    dataIndex: 'fase.denominazione',
    key: 'fase.denominazione',
    sorter: true,
    sortDataIndex: 'fase.denominazione',
    render: (_, record) => <TaskName>{record.fase?.denominazione}</TaskName>,
  },
  dataCreazione: {
    title: t('submission-columns.data-creazione', 'Data creazione'),
    dataIndex: 'dataCreazione',
    key: 'dataCreazione',
    sorter: true,
    sortDataIndex: 'dataCreazione',
    render: date => <ISODate date={date} format={dateFormat} />,
  },
  dataModifica: {
    title: t('submission-columns.data-modifica', 'Data modifica'),
    dataIndex: 'dataModifica',
    key: 'dataModifica',
    sorter: true,
    sortDataIndex: 'dataModifica',
    render: date => <ISODate date={date} format={dateFormat} />,
  },
  dataInvio: {
    title: t('submission-columns.data-invio', 'Data invio'),
    dataIndex: 'dataInvio',
    key: 'dataInvio',
    sorter: true,
    sortDataIndex: 'dataInvio',
    render: date => <ISODate date={date} format={dateFormat} />,
  },
  oggetto: {
    title: t('submission-columns.oggetto', 'Oggetto'),
    dataIndex: 'oggetto',
    key: 'oggetto',
    widthWithActiveScroll: 210,
    sortDataIndex: 'oggetto',
    render: (_, record) => (
      <TableCellEllipsis tooltip={false}>{record.oggetto}</TableCellEllipsis>
    ),
  },
  progressivo: {
    title: t('submission-columns.numero-istanza', 'Numero Istanza'),
    dataIndex: 'progressivo',
    key: 'progressivo',
    sorter: true,
    sortDataIndex: 'progressivo',
    render: (_, record) => record.progressivo,
  },
  protocollo: {
    title: t('submission-columns.numero-protocollo', 'Numero Protocollo'),
    dataIndex: 'numeroProtocollo',
    key: 'numeroProtocollo',
    sorter: true,
    sortDataIndex: 'numeroProtocollo',
    render: (_, record) => record.numeroProtocollo,
  },
  datiTitolari: {
    title: t('submission-columns.titolare', 'Titolare'),
    dataIndex: 'datiTitolari',
    key: 'datiTitolari',
    render: (_, record) => {
      const applicants = SubmissionLogic.getApplicant(record);

      if (!applicants) return null;
      return <ApplicantsTableCell applicants={applicants} />;
    },
  },
  richiedente: {
    title: t('submission-columns.richiedente', 'Richiedente'),
    key: 'richiedente',
    render: (_, record) => SubmissionLogic.getSubmitterFullName(record),
  },
  ufficioResponsabile: {
    title: t('submission-columns.ufficio-responsabile', 'Ufficio responsabile'),
    dataIndex: 'ufficioResponsabileProcedimento',
    key: 'ufficioResponsabileProcedimento',
    render: (_, record) => record.ufficioResponsabileProcedimento,
  },
} satisfies Record<
  string,
  ColumnGroupType<RichiestaDTO> | ColumnType<RichiestaDTO>
>;

/**
 * Configuriamo quale colonna deve essere visualizzata come numero
 * identificativo
 */
const subsmissionNumberColumn = theme.showProtocolNumberAsSubmissionNumberColumn
  ? submissionsColumnsBase.protocollo
  : submissionsColumnsBase.progressivo;

export const submissionsColumns: ColumnsType<RichiestaDTO> = [
  submissionsColumnsBase.stato,
  ...(theme.showPhaseSubmissionColumn ? [submissionsColumnsBase.fase] : []),
  subsmissionNumberColumn,
  submissionsColumnsBase.titoloTipoDomanda,
  submissionsColumnsBase.oggetto,
  submissionsColumnsBase.datiTitolari,
  submissionsColumnsBase.richiedente,
  submissionsColumnsBase.ufficioResponsabile,
  submissionsColumnsBase.dataInvio,
  submissionsColumnsBase.dataCreazione,
  submissionsColumnsBase.dataModifica,
  ...SubmissionAgidColumns,
];

export const submissionsColumnsMobile: ColumnsType<RichiestaDTO> = [
  submissionsColumnsBase.stato,
  submissionsColumnsBase.titoloTipoDomanda,
  submissionsColumnsBase.dataInvio,
];
